import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { ArrowDownIcon, ArrowUpIcon, UsersIcon, UserIcon, UserMinusIcon } from 'lucide-react';
import ReportLayout from '../core/ReportLayout';
import { formatCurrency, formatPercentage, formatDate } from '../shared/utils';
import { exportToPDF, exportToExcel } from '../shared/exportUtils';
import { FileText } from 'lucide-react';
import { successToast, errorToast } from '../../../lib/helpers';

const AreaAnalysis = forwardRef(function AreaAnalysis({ data, isLoading, searchTerm, hideHeader = false }, ref) {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const effectiveSearchTerm = searchTerm || localSearchTerm;

    const filteredData = React.useMemo(() => {
        if (!data?.data) return [];
        if (!effectiveSearchTerm) return data.data;

        const lowerSearchTerm = effectiveSearchTerm.toLowerCase();

        return data.data.filter(area => {
            if (area.area.toLowerCase().includes(lowerSearchTerm)) return true;
            const hasMatchingCustomer = area.customers.some(customer =>
                customer.customerName.toLowerCase().includes(lowerSearchTerm) ||
                customer.customerCode.toLowerCase().includes(lowerSearchTerm)
            );
            return hasMatchingCustomer;
        });
    }, [data, effectiveSearchTerm]);

    // Calculate overall totals
    const overallTotals = React.useMemo(() => {
        if (!data?.data) return null;
        
        return data.data.reduce((acc, area) => ({
            totalSales: acc.totalSales + (area.currentPeriod?.sales || 0),
            totalMargin: acc.totalMargin + (area.currentPeriod?.marginValue || 0),
            totalCustomers: acc.totalCustomers + (area.totalCustomers || 0),
            activeCustomers: acc.activeCustomers + (area.currentPeriod?.activeCustomers || 0),
            areaCount: acc.areaCount + 1
        }), { totalSales: 0, totalMargin: 0, totalCustomers: 0, activeCustomers: 0, areaCount: 0 });
    }, [data]);

    // Calculate top 6 areas by sales
    const topAreas = React.useMemo(() => {
        if (!data?.data) return [];
        
        return data.data
            .map(area => ({
                area: area.area,
                sales: area.currentPeriod?.sales || 0,
                margin: area.currentPeriod?.marginValue || 0,
                marginPercent: area.currentPeriod?.marginPercentage || 0,
                customers: area.totalCustomers || 0,
                activeCustomers: area.currentPeriod?.activeCustomers || 0
            }))
            .sort((a, b) => b.sales - a.sales)
            .slice(0, 6);
    }, [data]);

    const getChangeIndicator = (currentValue, previousValue, value) => {
        if (currentValue === 0 && previousValue === 0) return <span className="text-gray-500">N/A</span>;
        if (previousValue === 0 && currentValue > 0) return <span className="text-blue-600">NEW</span>;
        if (previousValue === 0 && currentValue < 0) return <span className="text-red-600">NEW</span>;
        if (currentValue === 0 && previousValue > 0) return <span className="text-red-600">-100.0%</span>;
        if (!value && value !== 0) return null;

        const color = value > 0 ? 'text-green-600' : value < 0 ? 'text-red-600' : 'text-gray-600';
        const Icon = value > 0 ? ArrowUpIcon : value < 0 ? ArrowDownIcon : null;
        return (
            <div className={`flex items-center gap-1 ${color}`}>
                {Icon && <Icon size={16} />}
                <span>{Math.abs(value).toFixed(1)}%</span>
            </div>
        );
    };

    const prepareExportData = () => {
        const tableData = [];
        filteredData.forEach(area => {
            area.customers.forEach(customer => {
                tableData.push({
                    area: area.area,
                    code: customer.customerCode || 'N/A',
                    customer: customer.customerName || 'N/A',
                    currentSales: customer.currentPeriod.sales || 0,
                    previousSales: customer.previousPeriod.sales || 0,
                    change: customer.comparison.salesChange || 0,
                    margin: customer.currentPeriod.marginPercentage || 0
                });
            });
        });

        return {
            tableData,
            columns: [
                { key: 'area', label: 'Area', format: 'text' },
                { key: 'code', label: 'Code', format: 'text' },
                { key: 'customer', label: 'Customer', format: 'text' },
                { key: 'currentSales', label: 'Current Sales', format: 'currency' },
                { key: 'previousSales', label: 'Previous Sales', format: 'currency' },
                { key: 'change', label: 'Change %', format: 'percentage' },
                { key: 'margin', label: 'Margin %', format: 'percentage' }
            ]
        };
    };

    const handleExportPDF = () => {
        try {
            const exportData = prepareExportData();
            exportToPDF({
                title: 'Area Analysis Report',
                subtitle: `Period: ${data.periodInfo ? formatDate(data.periodInfo.current.start) + ' - ' + formatDate(data.periodInfo.current.end) : ''}`,
                filename: `area-analysis-${new Date().toISOString().split('T')[0]}.pdf`,
                columns: exportData.columns,
                tableData: exportData.tableData
            });
            successToast('PDF exported successfully');
        } catch (error) {
            console.error('PDF export failed:', error);
            errorToast('Failed to export PDF');
        }
    };

    const handleExportExcel = () => {
        try {
            const exportData = prepareExportData();
            exportToExcel(exportData.tableData, {
                title: 'Area Analysis Report',
                filename: `area-analysis-${new Date().toISOString().split('T')[0]}.xlsx`,
                columns: exportData.columns
            });
            successToast('Excel file exported successfully');
        } catch (error) {
            console.error('Excel export failed:', error);
            errorToast('Failed to export Excel file');
        }
    };

    const handleEmailReport = () => {
        errorToast('Email functionality coming soon');
    };

    // Expose export handlers via ref
    useImperativeHandle(ref, () => ({
        handleExportPDF,
        handleExportExcel,
        handleEmailReport
    }));

    if (isLoading) {
        if (hideHeader) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Area Analysis"
                subtitle="Sales analysis by area"
                loading={isLoading}
                icon={FileText}
                iconColor="green"
            >
                <div className="flex items-center justify-center py-12">
                    <div className="text-gray-500">Loading sales data...</div>
                </div>
            </ReportLayout>
        );
    }

    if (!data || !data.periodInfo || !data.data) {
        if (hideHeader) {
            return (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            );
        }
        return (
            <ReportLayout
                title="Area Analysis"
                subtitle="Sales analysis by area"
                icon={FileText}
                iconColor="green"
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onEmailReport={handleEmailReport}
            >
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                    <p className="text-gray-500">No data available</p>
                </div>
            </ReportLayout>
        );
    }

    const content = (
            <div className="space-y-4">
                {/* Overall KPI Cards - Only show when not using hideHeader (standalone mode) */}
                {!hideHeader && overallTotals && (
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Sales</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.totalSales)}</div>
                            <div className="text-xs text-gray-500 mt-1">All Areas</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Margin</div>
                            <div className="text-2xl font-bold text-gray-900">{formatCurrency(overallTotals.totalMargin)}</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {overallTotals.totalSales > 0 ? formatPercentage((overallTotals.totalMargin / overallTotals.totalSales) * 100) : '0%'} margin
                            </div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Areas</div>
                            <div className="text-2xl font-bold text-gray-900">{overallTotals.areaCount}</div>
                            <div className="text-xs text-gray-500 mt-1">Areas</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Total Customers</div>
                            <div className="text-2xl font-bold text-gray-900">{overallTotals.totalCustomers.toLocaleString('en-GB')}</div>
                            <div className="text-xs text-gray-500 mt-1">All customers</div>
                        </div>
                        <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-red-500">
                            <div className="text-sm font-medium text-gray-600 mb-1">Active Customers</div>
                            <div className="text-2xl font-bold text-gray-900">{overallTotals.activeCustomers.toLocaleString('en-GB')}</div>
                            <div className="text-xs text-gray-500 mt-1">
                                {overallTotals.totalCustomers > 0 ? formatPercentage((overallTotals.activeCustomers / overallTotals.totalCustomers) * 100) : '0%'} active
                            </div>
                        </div>
                    </div>
                )}

                {/* Top 6 Areas KPI Cards - Only show when not using hideHeader (standalone mode) */}
                {!hideHeader && topAreas.length > 0 && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top 6 Areas by Sales</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {topAreas.map((areaData, index) => {
                                const colors = [
                                    { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', text: 'text-yellow-700', textDark: 'text-yellow-900', textLight: 'text-yellow-600', badge: 'bg-yellow-500' },
                                    { bg: 'from-gray-50 to-gray-100', border: 'border-gray-200', text: 'text-gray-700', textDark: 'text-gray-900', textLight: 'text-gray-600', badge: 'bg-gray-500' },
                                    { bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700', textDark: 'text-amber-900', textLight: 'text-amber-600', badge: 'bg-amber-500' },
                                    { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', text: 'text-blue-700', textDark: 'text-blue-900', textLight: 'text-blue-600', badge: 'bg-blue-500' },
                                    { bg: 'from-green-50 to-green-100', border: 'border-green-200', text: 'text-green-700', textDark: 'text-green-900', textLight: 'text-green-600', badge: 'bg-green-500' },
                                    { bg: 'from-purple-50 to-purple-100', border: 'border-purple-200', text: 'text-purple-700', textDark: 'text-purple-900', textLight: 'text-purple-600', badge: 'bg-purple-500' }
                                ];
                                const color = colors[index] || colors[0];
                                
                                return (
                                    <div key={areaData.area} className={`bg-gradient-to-br ${color.bg} rounded-lg p-4 border ${color.border} hover:shadow-md transition-shadow`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className={`text-xs font-medium ${color.text} px-2 py-1 rounded ${color.badge} text-white`}>
                                                #{index + 1}
                                            </div>
                                        </div>
                                        <div className={`text-sm font-semibold ${color.textDark} mb-1 truncate`} title={areaData.area}>{areaData.area}</div>
                                        <div className={`text-xl font-bold ${color.textDark}`}>{formatCurrency(areaData.sales)}</div>
                                        <div className={`text-xs ${color.textLight} mt-1`}>
                                            {areaData.customers.toLocaleString('en-GB')} customers
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Period Info Header */}
                <div className="bg-white rounded-lg shadow p-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Current Period</h3>
                            <p className="text-gray-600">
                                {formatDate(data.periodInfo.current.start)} - {formatDate(data.periodInfo.current.end)}
                            </p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-2">Previous Period</h3>
                            <p className="text-gray-600">
                                {formatDate(data.periodInfo.previous.start)} - {formatDate(data.periodInfo.previous.end)}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Area Cards */}
                <div className="grid gap-4">
                    {filteredData.map((area) => (
                        <div key={area.area} className="bg-white rounded-lg shadow">
                            <div className="p-4">
                                <div className="space-y-2 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-xl font-bold">{area.area}</h3>
                                        <div className="flex items-center gap-2">
                                            <UsersIcon className="text-gray-400" size={20} />
                                            <span className="text-lg font-semibold">{area.totalCustomers.toLocaleString()} Total Customers</span>
                                        </div>
                                    </div>
                                    <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
                                        <div
                                            className="h-full bg-green-500 transition-all duration-500"
                                            style={{ width: `${area.currentPeriod.activeCustomerPercentage}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Metrics Grid */}
                                <div className="grid grid-cols-4 gap-4 mb-4">
                                    <div className="bg-green-50 rounded-lg p-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-sm text-gray-600">Active Customers</p>
                                                <p className="text-2xl font-bold">{area.currentPeriod.activeCustomers.toLocaleString()}</p>
                                                <p className="text-sm text-gray-600">
                                                    {formatPercentage(area.currentPeriod.activeCustomerPercentage)}
                                                </p>
                                            </div>
                                            <UserIcon className="text-green-600" size={24} />
                                        </div>
                                        {getChangeIndicator(area.currentPeriod.activeCustomers, area.previousPeriod.activeCustomers, area.comparison.activeCustomerChange)}
                                    </div>

                                    <div className="bg-red-50 rounded-lg p-4">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-sm text-gray-600">Inactive Customers</p>
                                                <p className="text-2xl font-bold">{area.currentPeriod.inactiveCustomers.toLocaleString()}</p>
                                                <p className="text-sm text-gray-600">
                                                    {formatPercentage(100 - area.currentPeriod.activeCustomerPercentage)}
                                                </p>
                                            </div>
                                            <UserMinusIcon className="text-red-600" size={24} />
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <div>
                                            <p className="text-sm text-gray-600">Sales</p>
                                            <p className="text-2xl font-bold">{formatCurrency(area.currentPeriod.sales)}</p>
                                            <p className="text-sm text-gray-600">vs {formatCurrency(area.previousPeriod.sales)}</p>
                                        </div>
                                        {getChangeIndicator(area.currentPeriod.sales, area.previousPeriod.sales, area.comparison.salesChange)}
                                    </div>

                                    <div className="bg-purple-50 rounded-lg p-4">
                                        <div>
                                            <p className="text-sm text-gray-600">Margin</p>
                                            <p className="text-2xl font-bold">{formatCurrency(area.currentPeriod.marginValue)}</p>
                                            <p className="text-sm text-gray-600">{formatPercentage(area.currentPeriod.marginPercentage)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer Details Section */}
                                <div className="border rounded-md">
                                    <button
                                        className="w-full px-4 py-2 text-left text-sm font-semibold flex justify-between items-center hover:bg-gray-50"
                                        onClick={() => setLocalSearchTerm(prev => prev ? '' : area.area)}
                                    >
                                        View Customer Details
                                        <ArrowDownIcon
                                            className={`h-5 w-5 transition-transform ${effectiveSearchTerm === area.area ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    <div
                                        className={`overflow-hidden transition-all ${effectiveSearchTerm === area.area ? 'max-h-[2000px]' : 'max-h-0'}`}
                                    >
                                        <div className="p-4">
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Sales</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Sales</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Margin</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {area.customers
                                                            .sort((a, b) => b.currentPeriod.sales - a.currentPeriod.sales)
                                                            .map((customer) => (
                                                                <tr key={customer.customerCode} className="hover:bg-gray-50">
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{customer.customerCode}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{customer.customerName}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(customer.currentPeriod.sales)}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{formatCurrency(customer.previousPeriod.sales)}</td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                        {getChangeIndicator(
                                                                            customer.currentPeriod.sales,
                                                                            customer.previousPeriod.sales,
                                                                            customer.comparison.salesChange
                                                                        )}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                        {formatPercentage(customer.currentPeriod.marginPercentage)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
    );

    if (hideHeader) {
        return <div>{content}</div>;
    }

    return (
        <ReportLayout
            title="Area Analysis"
            subtitle="Sales analysis by area"
            icon={FileText}
            iconColor="green"
            onExportPDF={handleExportPDF}
            onExportExcel={handleExportExcel}
            onEmailReport={handleEmailReport}
        >
            {content}
        </ReportLayout>
    );
});

export default AreaAnalysis;

